import React, { useEffect, useState } from 'react';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { be1Fetch } from '../../config/api';
import { useTranslation } from 'react-i18next';
import { errorFromResponse } from '../../services/errorText';
import { useCapabilities } from '../../services/capabilities';

interface VarItem { name: string; value: string; description: string }
interface Props { onClose: () => void }

/**
 * 전역변수 관리 — 모든 워크플로의 문자열 속성(SQL·명령·경로·파라미터 값)에서 ${이름} 으로 참조한다.
 * 백엔드 /api/variables (DAG 폴더의 openflow_variables.json) 에 저장되어 Airflow 실행 시에도 같은 값이 쓰인다.
 * 우선순위: DAG 실행 conf > 노드 변수/파라미터 > 전역변수.
 */
const GlobalVariablesDialog: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  useEscapeKey(onClose);
  const [items, setItems] = useState<VarItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [file, setFile] = useState('');
  // 전역변수는 모든 워크플로의 외부명령어·SQL 에 들어간다 — 서버가 관리자만 저장을 받는다. 다른 사람은 보기만
  const caps = useCapabilities();
  const readOnly = !!caps && !caps.admin;

  useEffect(() => {
    be1Fetch(`/api/variables`).then(async (r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json(); setItems((j.items || []).map((i: any) => ({ name: i.name || '', value: i.value ?? '', description: i.description || '' }))); setFile(j.file || '');
    }).catch((e) => setError(t('dialog:vars.loadFailed', { message: e.message }))).finally(() => setLoading(false));
  }, [t]);

  const update = (i: number, p: Partial<VarItem>) => setItems((prev) => prev.map((r, k) => (k === i ? { ...r, ...p } : r)));
  const bad = (n: string) => !!n && !/^[A-Za-z_][A-Za-z0-9_]*$/.test(n);
  const dup = new Set(items.map((i) => i.name).filter((n, i, a) => n && a.indexOf(n) !== i));
  const canSave = !readOnly && !saving && items.every((i) => !bad(i.name)) && dup.size === 0;

  const save = async () => {
    setSaving(true); setError('');
    try {
      const r = await be1Fetch(`/api/variables`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: items.filter((i) => i.name.trim()) }) });
      if (!r.ok) throw await errorFromResponse(r, `HTTP ${r.status}`);
      onClose();
    } catch (e: any) { setError(t('dialog:vars.saveFailed', { message: e.message })); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-30" onMouseDown={onClose}>
      <div className="bg-white rounded shadow-lg w-[760px] max-w-[95vw] max-h-[85vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div>
            <div className="text-sm font-semibold text-gray-800">{t('dialog:vars.title')}</div>
            <div className="text-xs text-gray-500">{t('dialog:vars.sub1')}<code>{t('dialog:vars.nameRef')}</code>{t('dialog:vars.sub2')}</div>
          </div>
          <div className="flex items-center gap-1.5">
            {!readOnly && <button className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 text-gray-700" onClick={() => setItems((p) => [...p, { name: '', value: '', description: '' }])}>{t('dialog:vars.add')}</button>}
            <button className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={onClose} title={t('common:ui.close')}>✕</button>
          </div>
        </div>
        <div className="p-3 overflow-auto flex-1">
          {loading ? <div className="text-sm text-gray-500">{t('common:ui.loading')}</div> : items.length === 0 ? (
            <div className="text-sm text-gray-500 py-6 text-center">{t('dialog:vars.empty')}</div>
          ) : (
            <table className="w-full text-xs">
              <thead><tr className="text-left text-gray-500 border-b border-gray-200"><th className="py-1 px-1 w-[26%]">{t('common:ui.name')}</th><th className="py-1 px-1 w-[34%]">{t('dialog:vars.value')}</th><th className="py-1 px-1">{t('common:ui.description')}</th><th className="w-6"></th></tr></thead>
              <tbody>
                {items.map((r, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="py-1 px-1"><input className={`w-full border rounded px-1 py-0.5 bg-white font-mono ${bad(r.name) || dup.has(r.name) ? 'border-red-400' : 'border-gray-300'}`} disabled={readOnly} value={r.name} placeholder="NAME" onChange={(e) => update(i, { name: e.target.value })} /></td>
                    <td className="py-1 px-1"><input className="w-full border border-gray-300 rounded px-1 py-0.5 bg-white font-mono" disabled={readOnly} value={r.value} onChange={(e) => update(i, { value: e.target.value })} /></td>
                    <td className="py-1 px-1"><input className="w-full border border-gray-300 rounded px-1 py-0.5 bg-white" disabled={readOnly} value={r.description} onChange={(e) => update(i, { description: e.target.value })} /></td>
                    <td className="py-1 text-gray-500"><button className="hover:text-red-500 disabled:opacity-30" disabled={readOnly} title={t('common:ui.delete')} onClick={() => setItems((p) => p.filter((_, k) => k !== i))}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {readOnly && <div className="text-xs text-amber-700 mt-2">{t('dialog:vars.adminOnly')}</div>}
          {error && <div className="text-xs text-red-500 mt-2">{error}</div>}
          {dup.size > 0 && <div className="text-xs text-red-500 mt-2">{t('dialog:vars.duplicate', { names: Array.from(dup).join(', ') })}</div>}
        </div>
        <div className="px-4 py-2 border-t border-gray-200 flex items-center justify-between">
          <span className="text-[0.6875rem] text-gray-400 truncate" title={file}>{file}</span>
          <div className="flex gap-2">
            <button className="px-3 py-1 text-xs rounded bg-gray-200 hover:bg-gray-300 text-gray-700" onClick={onClose}>{t('common:ui.cancel')}</button>
            <button className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50" disabled={!canSave} onClick={save}>{saving ? t('dialog:vars.saving') : t('dialog:vars.save')}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalVariablesDialog;
